import { useForm, type FieldErrors } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { usePlatformAuth } from '@/contexts/PlatformAuthContext';
import { useToast } from '@/contexts/ToastContext';
import { platformApi } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { PasswordInput } from '@/components/ui/password-input';
import { FormError } from '@/components/ui/form-error';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { newPasswordRule, FIELD_MESSAGES, REQUIRED_FIELDS_MESSAGE } from '@/lib/validation-rules';
import { focusFirstInvalidField, handleApiSubmitError } from '@/lib/form-validation';

const schema = z.object({
  currentPassword: z.string().min(1, 'Current password is required.'),
  newPassword: newPasswordRule,
  confirmPassword: z.string().min(1, 'Confirm your password.'),
}).superRefine((data, ctx) => {
  if (data.newPassword !== data.confirmPassword) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['confirmPassword'], message: FIELD_MESSAGES.passwordMatch });
  }
});

type FormData = z.infer<typeof schema>;

export default function PlatformProfilePage() {
  const { user } = usePlatformAuth();
  const { addToast } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { register, handleSubmit, setError, formState: { errors, isSubmitting } } = useForm<FormData>({ resolver: zodResolver(schema) });

  const onInvalid = (errs: FieldErrors<FormData>) => {
    focusFirstInvalidField(errs);
    addToast(REQUIRED_FIELDS_MESSAGE, 'error');
  };

  const onSubmit = async (data: FormData) => {
    try {
      await platformApi.post('/platform/auth/change-password', { currentPassword: data.currentPassword, newPassword: data.newPassword });
      sessionStorage.removeItem("platform_access_token");
      queryClient.clear();
      addToast('Password changed. Please sign in again.', 'success');
      navigate('/super-admin/login');
    } catch (err: any) {
      handleApiSubmitError(err, { addToast, setError: (name, error) => setError(name as any, error) });
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <h1 className="text-2xl font-heading font-bold">My Profile</h1>
      <Card>
        <CardHeader><CardTitle className="text-base">Account Information</CardTitle></CardHeader>
        <CardContent className="space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-4">
            <div><span className="text-secondary">Name</span><p className="font-medium">{user?.name}</p></div>
            <div><span className="text-secondary">Email</span><p className="font-medium">{user?.email}</p></div>
            <div><span className="text-secondary">Scope</span><div className="flex gap-1 mt-1"><Badge variant="info">Platform Super Admin</Badge></div></div>
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle className="text-base">Change Password</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <form onSubmit={handleSubmit(onSubmit, onInvalid)} className="space-y-4">
            <div>
              <Label htmlFor="currentPassword">Current Password</Label>
              <PasswordInput
                id="currentPassword"
                {...register('currentPassword')}
                aria-invalid={errors.currentPassword ? true : undefined}
                aria-describedby={errors.currentPassword ? 'currentPassword-error' : undefined}
                className={cn(errors.currentPassword && 'border-error')}
              />
              <FormError id="currentPassword-error" message={errors.currentPassword?.message} />
            </div>
            <div>
              <Label htmlFor="newPassword">New Password</Label>
              <PasswordInput
                id="newPassword"
                {...register('newPassword')}
                aria-invalid={errors.newPassword ? true : undefined}
                aria-describedby={errors.newPassword ? 'newPassword-error' : undefined}
                className={cn(errors.newPassword && 'border-error')}
              />
              <FormError id="newPassword-error" message={errors.newPassword?.message} />
            </div>
            <div>
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <PasswordInput
                id="confirmPassword"
                {...register('confirmPassword')}
                aria-invalid={errors.confirmPassword ? true : undefined}
                aria-describedby={errors.confirmPassword ? 'confirmPassword-error' : undefined}
                className={cn(errors.confirmPassword && 'border-error')}
              />
              <FormError id="confirmPassword-error" message={errors.confirmPassword?.message} />
            </div>
            <Button type="submit" loading={isSubmitting}>Change Password</Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
